import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  FileText,
  Users,
  FolderKanban,
  ArrowLeft,
  Trash2,
  Edit,
  Loader2,
  Shield,
  User,
  Search,
  Clock,
  Activity,
  Download,
  Upload,
  Eye,
  TrendingUp,
  AlertTriangle,
  Calendar,
  Bell,
  Settings,
  BarChart3,
  RefreshCw,
  Plus,
  UserPlus,
  Mail,
  Copy,
  Building2,
  UserCog,
  X,
  GraduationCap
} from 'lucide-react';

// Import new admin panels
import EmailIngestionPanel from '../components/admin/EmailIngestionPanel';
import DuplicateDetectionPanel from '../components/admin/DuplicateDetectionPanel';
import TimeBoundAccessPanel from '../components/admin/TimeBoundAccessPanel';
import UserStudyAssignmentsPanel from '../components/admin/UserStudyAssignmentsPanel';
import TrainingManagementPanel from '../components/admin/TrainingManagementPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const { user, getAuthHeaders, isAdmin, impersonatedRole, startImpersonation, stopImpersonation, isImpersonating, getEffectiveRole } = useAuth();
  const navigate = useNavigate();

  // Role switcher state
  const [showRoleSwitcher, setShowRoleSwitcher] = useState(false);
  const [selectedImpersonationRole, setSelectedImpersonationRole] = useState('');

  // Existing state
  const [users, setUsers] = useState([]);
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editStudyModal, setEditStudyModal] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [studyName, setStudyName] = useState('');
  const [studyDescription, setStudyDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Study filter for dashboard and audit
  const [dashboardStudyFilter, setDashboardStudyFilter] = useState('all');
  const [auditStudyFilter, setAuditStudyFilter] = useState('all');
  
  // Add User Modal state
  const [addUserModal, setAddUserModal] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [newUserData, setNewUserData] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    role: 'cra', // Default to CRA (most common)
    site_id: '',
    organization: '',
    permissions: {
      can_view: true,
      can_upload: false,
      can_edit_metadata: false,
      can_edit_metadata_limited: false,
      can_delete: false,
      can_export: false,
      can_qc_review: false,
      can_approve: false,
      can_reject: false,
      can_sign: false,
      can_manage_workflows: false,
      can_create_study: false,
      can_edit_study: false,
      can_delete_study: false,
      can_manage_users: false,
      can_manage_roles: false,
      can_manage_settings: false,
      can_view_audit_logs: false,
      can_export_audit_logs: false,
      visible_folders: [],
      visible_studies: [],
      visible_sites: [],
      is_inspection_mode: false,
      is_time_limited: false,
      access_expires_at: null
    }
  });
  const [addingUser, setAddingUser] = useState(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditStats, setAuditStats] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  // Dashboard stats state
  const [dashboardStats, setDashboardStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [uploadsChart, setUploadsChart] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  // Expiration state
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [expiredDocs, setExpiredDocs] = useState([]);
  const [expirationSettings, setExpirationSettings] = useState({ warning_days: 30, critical_days: 7 });
  const [savingExpiration, setSavingExpiration] = useState(false);

  // Notification logs state
  const [notificationLogs, setNotificationLogs] = useState([]);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchData();
    fetchDashboardData(); // Load dashboard data on initial mount
    fetchAvailableRoles(); // Fetch available roles for user creation
  }, []);

  const fetchAvailableRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/roles`, getAuthHeaders());
      setAvailableRoles(response.data.roles || []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      // Fallback to default roles if API fails
      setAvailableRoles([
        { value: 'system_admin', label: 'System Administrator' },
        { value: 'tmf_lead', label: 'TMF Lead / Manager' },
        { value: 'ctm', label: 'Clinical Trial Manager (CTM)' },
        { value: 'cra', label: 'CRA / Monitor' },
        { value: 'cdm', label: 'Clinical Data Manager' },
        { value: 'qc_reviewer', label: 'QC Reviewer' },
        { value: 'qa_auditor', label: 'QA / Auditor' },
        { value: 'regulatory', label: 'Regulatory Affairs' },
        { value: 'site_user', label: 'Site User (PI / Coordinator)' },
        { value: 'vendor', label: 'CRO / Vendor User' },
        { value: 'inspector', label: 'Inspector (Read-Only)' },
      ]);
    }
  };

  // Update permissions when role changes
  const handleRoleChange = (newRole) => {
    const roleInfo = availableRoles.find(r => r.value === newRole);
    if (roleInfo) {
      // Map role permissions to form permissions
      const permissions = roleInfo.permissions || [];
      setNewUserData(prev => ({
        ...prev,
        role: newRole,
        permissions: {
          ...prev.permissions,
          can_view: permissions.includes('view'),
          can_upload: permissions.includes('upload'),
          can_edit_metadata: permissions.includes('edit_metadata'),
          can_edit_metadata_limited: permissions.includes('edit_metadata_limited'),
          can_delete: permissions.includes('delete'),
          can_export: permissions.includes('export'),
          can_qc_review: permissions.includes('qc_review'),
          can_approve: permissions.includes('approve'),
          can_reject: permissions.includes('reject'),
          can_sign: permissions.includes('sign'),
          can_manage_workflows: permissions.includes('manage_workflow'),
          can_create_study: permissions.includes('create_study'),
          can_edit_study: permissions.includes('edit_study'),
          can_delete_study: permissions.includes('delete_study'),
          can_manage_users: permissions.includes('manage_users'),
          can_manage_roles: permissions.includes('manage_roles'),
          can_manage_settings: permissions.includes('manage_settings'),
          can_view_audit_logs: permissions.includes('view_audit_logs'),
          can_export_audit_logs: permissions.includes('export_audit_logs'),
          is_inspection_mode: permissions.includes('inspection_mode'),
          is_time_limited: permissions.includes('time_limited_access'),
        }
      }));
    }
  };

  const fetchData = async () => {
    try {
      const [usersRes, studiesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`, getAuthHeaders()),
        axios.get(`${API_URL}/api/studies`, getAuthHeaders())
      ]);
      setUsers(usersRes.data);
      setStudies(studiesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async (studyId = 'all') => {
    setAuditLoading(true);
    try {
      const studyParam = studyId && studyId !== 'all' ? `&study_id=${studyId}` : '';
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/audit-logs?limit=100${studyParam}`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/audit-logs/stats${studyParam ? `?study_id=${studyId}` : ''}`, getAuthHeaders())
      ]);
      setAuditLogs(logsRes.data);
      setAuditStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setAuditLoading(false);
    }
  };

  const fetchDashboardData = async (studyId = 'all') => {
    setDashboardLoading(true);
    try {
      const studyParam = studyId && studyId !== 'all' ? `?study_id=${studyId}` : '';
      const studyParamAlt = studyId && studyId !== 'all' ? `&study_id=${studyId}` : '';
      
      const [statsRes, activityRes, chartRes, topUsersRes, expiringRes, expiredRes, settingsRes, notifLogsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/dashboard/stats${studyParam}`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/dashboard/activity?limit=20${studyParamAlt}`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/dashboard/uploads-chart?days=7${studyParamAlt}`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/dashboard/top-users?limit=5${studyParamAlt}`, getAuthHeaders()),
        axios.get(`${API_URL}/api/documents/expiring?days=30${studyParamAlt}`, getAuthHeaders()),
        axios.get(`${API_URL}/api/documents/expired${studyParam}`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/settings/expiration`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/notification-logs?limit=50${studyParamAlt}`, getAuthHeaders())
      ]);
      setDashboardStats(statsRes.data);
      setRecentActivity(activityRes.data);
      setUploadsChart(chartRes.data);
      setTopUsers(topUsersRes.data);
      setExpiringDocs(expiringRes.data);
      setExpiredDocs(expiredRes.data);
      setExpirationSettings(settingsRes.data);
      setNotificationLogs(notifLogsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setDashboardLoading(false);
    }
  };

  // Handle Add User
  const handleAddUser = async () => {
    if (!newUserData.name || !newUserData.email || !newUserData.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setAddingUser(true);
    try {
      await axios.post(`${API_URL}/api/admin/users`, {
        name: newUserData.name,
        email: newUserData.email,
        password: newUserData.password,
        role: newUserData.role,
        permissions: newUserData.permissions
      }, getAuthHeaders());
      
      toast.success('User added successfully');
      setAddUserModal(false);
      setNewUserData({ 
        name: '', email: '', password: '', role: 'viewer',
        permissions: {
          can_upload: false, can_review: false, can_approve: false,
          can_delete: false, can_edit_metadata: false, can_manage_workflows: false,
          can_sign: false, visible_folders: [], visible_studies: []
        }
      });
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add user';
      toast.error(message);
    } finally {
      setAddingUser(false);
    }
  };

  // Handle Edit User
  const handleEditUser = (userToEdit) => {
    setSelectedUser(userToEdit);
    setNewUserData({
      name: userToEdit.name,
      email: userToEdit.email,
      password: '',
      role: userToEdit.role,
      permissions: userToEdit.permissions || {
        can_upload: false, can_review: false, can_approve: false,
        can_delete: false, can_edit_metadata: false, can_manage_workflows: false,
        can_sign: false, visible_folders: [], visible_studies: []
      }
    });
    setEditUserModal(true);
  };

  // Handle Update User
  const handleUpdateUser = async () => {
    if (!newUserData.name) {
      toast.error('Name is required');
      return;
    }
    
    setAddingUser(true);
    try {
      await axios.put(`${API_URL}/api/admin/users/${selectedUser.id}`, {
        name: newUserData.name,
        role: newUserData.role,
        permissions: newUserData.permissions
      }, getAuthHeaders());
      
      toast.success('User updated successfully');
      setEditUserModal(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update user';
      toast.error(message);
    } finally {
      setAddingUser(false);
    }
  };

  // Handle Toggle User Active
  const handleToggleUserActive = async (userId) => {
    try {
      const res = await axios.put(`${API_URL}/api/admin/users/${userId}/toggle-active`, {}, getAuthHeaders());
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to change user status');
    }
  };

  // Update permissions based on role preset
  const applyRolePreset = (role) => {
    // Enhanced presets for 11 clinical roles + legacy roles
    const presets = {
      // New Clinical Roles
      system_admin: {
        can_view: true, can_upload: true, can_edit_metadata: true, can_edit_metadata_limited: false,
        can_delete: false, can_export: false, can_qc_review: false, can_approve: false, can_reject: false,
        can_sign: false, can_manage_workflows: true, can_create_study: true, can_edit_study: true,
        can_delete_study: true, can_manage_users: true, can_manage_roles: true, can_manage_settings: true,
        can_view_audit_logs: true, can_export_audit_logs: true, visible_folders: [], visible_studies: [], visible_sites: [],
        is_inspection_mode: false, is_time_limited: false, access_expires_at: null
      },
      tmf_lead: {
        can_view: true, can_upload: true, can_edit_metadata: true, can_edit_metadata_limited: false,
        can_delete: false, can_export: true, can_qc_review: true, can_approve: true, can_reject: true,
        can_sign: true, can_manage_workflows: true, can_create_study: true, can_edit_study: true,
        can_delete_study: false, can_manage_users: false, can_manage_roles: false, can_manage_settings: false,
        can_view_audit_logs: true, can_export_audit_logs: false, visible_folders: [], visible_studies: [], visible_sites: [],
        is_inspection_mode: false, is_time_limited: false, access_expires_at: null
      },
      ctm: {
        can_view: true, can_upload: true, can_edit_metadata: true, can_edit_metadata_limited: false,
        can_delete: false, can_export: true, can_qc_review: false, can_approve: false, can_reject: false,
        can_sign: false, can_manage_workflows: true, can_create_study: true, can_edit_study: true,
        can_delete_study: false, can_manage_users: false, can_manage_roles: false, can_manage_settings: false,
        can_view_audit_logs: false, can_export_audit_logs: false, visible_folders: [], visible_studies: [], visible_sites: [],
        is_inspection_mode: false, is_time_limited: false, access_expires_at: null
      },
      cra: {
        can_view: true, can_upload: true, can_edit_metadata: false, can_edit_metadata_limited: true,
        can_delete: false, can_export: false, can_qc_review: false, can_approve: false, can_reject: false,
        can_sign: false, can_manage_workflows: false, can_create_study: false, can_edit_study: false,
        can_delete_study: false, can_manage_users: false, can_manage_roles: false, can_manage_settings: false,
        can_view_audit_logs: false, can_export_audit_logs: false, visible_folders: [], visible_studies: [], visible_sites: [],
        is_inspection_mode: false, is_time_limited: false, access_expires_at: null
      },
      cdm: {
        can_view: true, can_upload: true, can_edit_metadata: true, can_edit_metadata_limited: false,
        can_delete: false, can_export: true, can_qc_review: false, can_approve: false, can_reject: false,
        can_sign: false, can_manage_workflows: true, can_create_study: false, can_edit_study: false,
        can_delete_study: false, can_manage_users: false, can_manage_roles: false, can_manage_settings: false,
        can_view_audit_logs: false, can_export_audit_logs: false, visible_folders: [], visible_studies: [], visible_sites: [],
        is_inspection_mode: false, is_time_limited: false, access_expires_at: null
      },
      qc_reviewer: {
        can_view: true, can_upload: false, can_edit_metadata: true, can_edit_metadata_limited: false,
        can_delete: false, can_export: false, can_qc_review: true, can_approve: true, can_reject: true,
        can_sign: true, can_manage_workflows: false, can_create_study: false, can_edit_study: false,
        can_delete_study: false, can_manage_users: false, can_manage_roles: false, can_manage_settings: false,
        can_view_audit_logs: false, can_export_audit_logs: false, visible_folders: [], visible_studies: [], visible_sites: [],
        is_inspection_mode: false, is_time_limited: false, access_expires_at: null
      },
      qa_auditor: {
        can_view: true, can_upload: false, can_edit_metadata: false, can_edit_metadata_limited: false,
        can_delete: false, can_export: true, can_qc_review: false, can_approve: false, can_reject: false,
        can_sign: false, can_manage_workflows: false, can_create_study: false, can_edit_study: false,
        can_delete_study: false, can_manage_users: false, can_manage_roles: false, can_manage_settings: false,
        can_view_audit_logs: true, can_export_audit_logs: true, visible_folders: [], visible_studies: [], visible_sites: [],
        is_inspection_mode: false, is_time_limited: false, access_expires_at: null
      },
      regulatory: {
        can_view: true, can_upload: true, can_edit_metadata: true, can_edit_metadata_limited: false,
        can_delete: false, can_export: true, can_qc_review: true, can_approve: true, can_reject: true,
        can_sign: true, can_manage_workflows: false, can_create_study: false, can_edit_study: false,
        can_delete_study: false, can_manage_users: false, can_manage_roles: false, can_manage_settings: false,
        can_view_audit_logs: true, can_export_audit_logs: false, visible_folders: [], visible_studies: [], visible_sites: [],
        is_inspection_mode: false, is_time_limited: false, access_expires_at: null
      },
      site_user: {
        can_view: true, can_upload: true, can_edit_metadata: false, can_edit_metadata_limited: true,
        can_delete: false, can_export: false, can_qc_review: false, can_approve: false, can_reject: false,
        can_sign: false, can_manage_workflows: false, can_create_study: false, can_edit_study: false,
        can_delete_study: false, can_manage_users: false, can_manage_roles: false, can_manage_settings: false,
        can_view_audit_logs: false, can_export_audit_logs: false, visible_folders: [], visible_studies: [], visible_sites: [],
        is_inspection_mode: false, is_time_limited: false, access_expires_at: null
      },
      vendor: {
        can_view: true, can_upload: true, can_edit_metadata: false, can_edit_metadata_limited: true,
        can_delete: false, can_export: false, can_qc_review: false, can_approve: false, can_reject: false,
        can_sign: false, can_manage_workflows: false, can_create_study: false, can_edit_study: false,
        can_delete_study: false, can_manage_users: false, can_manage_roles: false, can_manage_settings: false,
        can_view_audit_logs: false, can_export_audit_logs: false, visible_folders: [], visible_studies: [], visible_sites: [],
        is_inspection_mode: false, is_time_limited: false, access_expires_at: null
      },
      inspector: {
        can_view: true, can_upload: false, can_edit_metadata: false, can_edit_metadata_limited: false,
        can_delete: false, can_export: true, can_qc_review: false, can_approve: false, can_reject: false,
        can_sign: false, can_manage_workflows: false, can_create_study: false, can_edit_study: false,
        can_delete_study: false, can_manage_users: false, can_manage_roles: false, can_manage_settings: false,
        can_view_audit_logs: true, can_export_audit_logs: false, visible_folders: [], visible_studies: [], visible_sites: [],
        is_inspection_mode: true, is_time_limited: true, access_expires_at: null
      },
      // Legacy roles for backward compatibility
      admin: {
        can_view: true, can_upload: true, can_edit_metadata: true, can_edit_metadata_limited: false,
        can_delete: true, can_export: true, can_qc_review: true, can_approve: true, can_reject: true,
        can_sign: true, can_manage_workflows: true, can_create_study: true, can_edit_study: true,
        can_delete_study: true, can_manage_users: true, can_manage_roles: true, can_manage_settings: true,
        can_view_audit_logs: true, can_export_audit_logs: true, visible_folders: [], visible_studies: [], visible_sites: [],
        is_inspection_mode: false, is_time_limited: false, access_expires_at: null
      },
      editor: {
        can_view: true, can_upload: true, can_edit_metadata: true, can_edit_metadata_limited: false,
        can_delete: false, can_export: true, can_qc_review: true, can_approve: false, can_reject: false,
        can_sign: true, can_manage_workflows: true, can_create_study: false, can_edit_study: false,
        can_delete_study: false, can_manage_users: false, can_manage_roles: false, can_manage_settings: false,
        can_view_audit_logs: false, can_export_audit_logs: false, visible_folders: [], visible_studies: [], visible_sites: [],
        is_inspection_mode: false, is_time_limited: false, access_expires_at: null
      },
      viewer: {
        can_view: true, can_upload: false, can_edit_metadata: false, can_edit_metadata_limited: false,
        can_delete: false, can_export: false, can_qc_review: false, can_approve: false, can_reject: false,
        can_sign: false, can_manage_workflows: false, can_create_study: false, can_edit_study: false,
        can_delete_study: false, can_manage_users: false, can_manage_roles: false, can_manage_settings: false,
        can_view_audit_logs: false, can_export_audit_logs: false, visible_folders: [], visible_studies: [], visible_sites: [],
        is_inspection_mode: false, is_time_limited: false, access_expires_at: null
      }
    };
    setNewUserData(prev => ({
      ...prev,
      role,
      permissions: presets[role] || presets.viewer
    }));
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/role`, { role: newRole }, getAuthHeaders());
      toast.success('Role updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, getAuthHeaders());
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleEditStudy = (study) => {
    setSelectedStudy(study);
    setStudyName(study.name);
    setStudyDescription(study.description);
    setEditStudyModal(true);
  };

  const handleSaveStudy = async () => {
    if (!studyName) {
      toast.error('Study name is required');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/studies/${selectedStudy.id}`, {
        name: studyName,
        description: studyDescription
      }, getAuthHeaders());
      toast.success('Study updated successfully');
      setEditStudyModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update study');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStudy = async (studyId) => {
    if (!window.confirm('Are you sure you want to delete this study? All associated folders and documents will be deleted.')) return;

    try {
      await axios.delete(`${API_URL}/api/studies/${studyId}`, getAuthHeaders());
      toast.success('Study deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete study');
    }
  };

  const handleSaveExpirationSettings = async () => {
    setSavingExpiration(true);
    try {
      await axios.put(`${API_URL}/api/admin/settings/expiration`, expirationSettings, getAuthHeaders());
      toast.success('Expiration settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingExpiration(false);
    }
  };

  const handleTriggerExpirationCheck = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/check-expirations`, {}, getAuthHeaders());
      toast.success('Expiration check completed, notifications sent');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to check expirations');
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      // New Clinical Roles
      case 'system_admin': return 'bg-red-500/30 text-red-300';
      case 'tmf_lead': return 'bg-purple-500/30 text-purple-300';
      case 'ctm': return 'bg-blue-500/30 text-blue-300';
      case 'cra': return 'bg-teal-500/30 text-teal-300';
      case 'cdm': return 'bg-green-500/30 text-green-300';
      case 'qc_reviewer': return 'bg-orange-500/30 text-orange-300';
      case 'qa_auditor': return 'bg-amber-500/30 text-amber-300';
      case 'regulatory': return 'bg-indigo-500/30 text-indigo-300';
      case 'site_user': return 'bg-cyan-500/30 text-cyan-300';
      case 'vendor': return 'bg-slate-500/30 text-slate-300';
      case 'inspector': return 'bg-red-600/30 text-red-400';
      // Legacy roles
      case 'admin': return 'bg-red-500/30 text-red-300';
      case 'editor': return 'bg-teal-500/30 text-teal-300';
      default: return 'bg-slate-200 text-slate-600';
    }
  };

  // Role display names mapping
  const getRoleDisplayName = (role) => {
    const roleNames = {
      system_admin: 'System Admin',
      tmf_lead: 'TMF Lead',
      ctm: 'CTM',
      cra: 'CRA',
      cdm: 'Data Manager',
      qc_reviewer: 'QC Reviewer',
      qa_auditor: 'QA Auditor',
      regulatory: 'Regulatory',
      site_user: 'Site User',
      vendor: 'Vendor',
      inspector: 'Inspector',
      admin: 'Admin',
      editor: 'Editor',
      viewer: 'Viewer'
    };
    return roleNames[role] || role;
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredStudies = studies.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getActivityIcon = (action) => {
    switch (action) {
      case 'upload': return <Upload className="w-4 h-4 text-green-600" />;
      case 'download': return <Download className="w-4 h-4 text-red-600" />;
      case 'login': return <User className="w-4 h-4 text-purple-600" />;
      default: return <Activity className="w-4 h-4 text-slate-600" />;
    }
  };

  const maxUploads = Math.max(...uploadsChart.map(d => d.uploads), 1);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-50 to-slate-100">
      {/* Impersonation Banner */}
      {isImpersonating() && (
        <div className="bg-amber-500 text-amber-900 px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span className="font-medium">Viewing as: {getRoleDisplayName(getEffectiveRole())}</span>
            <span className="text-amber-800 text-sm">(Your actual role: {getRoleDisplayName(user?.role)})</span>
          </div>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={stopImpersonation}
            className="text-amber-900 hover:bg-amber-400"
          >
            <X className="w-4 h-4 mr-1" />
            Exit View Mode
          </Button>
        </div>
      )}
      
      {/* Header - Dark Blue */}
      <header className="h-16 px-6 flex items-center justify-between bg-gradient-to-r from-slate-900 to-slate-800 border-b border-slate-700/50 backdrop-blur-lg shadow-lg">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="rounded-full text-white/70 hover:text-white hover:bg-slate-50/10"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <div 
          className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/dashboard?view=all')}
          title="Go to All Documents"
        >
          <div className="w-9 h-9 rounded-lg bg-red-600 flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Admin Panel
          </span>
        </div>

        {/* Role Switcher Button */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRoleSwitcher(true)}
            className="border-slate-600 text-white hover:bg-slate-700 flex items-center gap-2"
            data-testid="role-switcher-btn"
          >
            <UserCog className="w-4 h-4" />
            View as Role
          </Button>
        </div>
      </header>

      {/* Role Switcher Dialog */}
      <Dialog open={showRoleSwitcher} onOpenChange={setShowRoleSwitcher}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-teal-400" />
              View as Different Role
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-white/70 text-sm">
              As a System Admin, you can temporarily view the application as any other role to understand what users with that role can see and do.
            </p>
            
            <div>
              <Label className="text-white mb-2">Select Role to View As:</Label>
              <Select value={selectedImpersonationRole} onValueChange={setSelectedImpersonationRole}>
                <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white [&>span]:text-white">
                  <SelectValue placeholder="Choose a role..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="tmf_lead" className="text-white hover:bg-slate-700">TMF Lead / Manager</SelectItem>
                  <SelectItem value="ctm" className="text-white hover:bg-slate-700">Clinical Trial Manager (CTM)</SelectItem>
                  <SelectItem value="cra" className="text-white hover:bg-slate-700">CRA / Monitor</SelectItem>
                  <SelectItem value="cdm" className="text-white hover:bg-slate-700">Clinical Data Manager</SelectItem>
                  <SelectItem value="qc_reviewer" className="text-white hover:bg-slate-700">QC Reviewer</SelectItem>
                  <SelectItem value="qa_auditor" className="text-white hover:bg-slate-700">QA / Auditor</SelectItem>
                  <SelectItem value="regulatory" className="text-white hover:bg-slate-700">Regulatory Affairs</SelectItem>
                  <SelectItem value="site_user" className="text-white hover:bg-slate-700">Site User (PI / Coordinator)</SelectItem>
                  <SelectItem value="vendor" className="text-white hover:bg-slate-700">CRO / Vendor User</SelectItem>
                  <SelectItem value="inspector" className="text-white hover:bg-slate-700">Inspector (Read-Only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {isImpersonating() && (
              <div className="p-3 bg-amber-500/20 border border-amber-500/30 rounded-lg">
                <p className="text-amber-300 text-sm">
                  Currently viewing as: <strong>{getRoleDisplayName(getEffectiveRole())}</strong>
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            {isImpersonating() && (
              <Button 
                variant="outline" 
                onClick={() => { stopImpersonation(); setShowRoleSwitcher(false); toast.success('Returned to System Admin view'); }}
                className="border-slate-600 text-white hover:bg-slate-700"
              >
                Return to Admin View
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => setShowRoleSwitcher(false)}
              className="border-slate-600 text-white hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (selectedImpersonationRole) {
                  startImpersonation(selectedImpersonationRole);
                  setShowRoleSwitcher(false);
                  toast.success(`Now viewing as ${getRoleDisplayName(selectedImpersonationRole)}`);
                  navigate('/dashboard');
                } else {
                  toast.error('Please select a role');
                }
              }}
              className="bg-teal-600 hover:bg-teal-700"
              disabled={!selectedImpersonationRole}
            >
              Start Viewing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="dashboard" className="w-full" onValueChange={(val) => {
          if (val === 'audit') fetchAuditLogs(auditStudyFilter);
          if (val === 'dashboard') fetchDashboardData(dashboardStudyFilter);
        }}>
          <div className="flex items-center justify-between mb-6">
            <TabsList className="bg-slate-100/80 border border-slate-300">
              <TabsTrigger value="dashboard" className="text-slate-700 data-[state=active]:bg-red-600 data-[state=active]:text-slate-900">
                <BarChart3 className="w-4 h-4 mr-2" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="users" className="text-slate-700 data-[state=active]:bg-red-600 data-[state=active]:text-slate-900">
                <Users className="w-4 h-4 mr-2" />
                Users
              </TabsTrigger>
              <TabsTrigger value="studies" className="text-slate-700 data-[state=active]:bg-red-600 data-[state=active]:text-slate-900">
                <FolderKanban className="w-4 h-4 mr-2" />
                Studies
              </TabsTrigger>
              <TabsTrigger value="audit" className="text-slate-700 data-[state=active]:bg-red-600 data-[state=active]:text-slate-900">
                <Activity className="w-4 h-4 mr-2" />
                Audit Logs
              </TabsTrigger>
              <TabsTrigger value="notifications" className="text-slate-700 data-[state=active]:bg-red-600 data-[state=active]:text-slate-900">
                <Bell className="w-4 h-4 mr-2" />
                Notifications
              </TabsTrigger>
              <TabsTrigger value="duplicates" className="text-slate-700 data-[state=active]:bg-red-600 data-[state=active]:text-slate-900">
                <Copy className="w-4 h-4 mr-2" />
                Duplicates
              </TabsTrigger>
              <TabsTrigger value="email-ingestion" className="text-slate-700 data-[state=active]:bg-red-600 data-[state=active]:text-slate-900">
                <Mail className="w-4 h-4 mr-2" />
                Email
              </TabsTrigger>
              <TabsTrigger value="time-access" className="text-slate-700 data-[state=active]:bg-red-600 data-[state=active]:text-slate-900">
                <Clock className="w-4 h-4 mr-2" />
                Access
              </TabsTrigger>
              <TabsTrigger value="study-assignments" className="text-slate-700 data-[state=active]:bg-red-600 data-[state=active]:text-slate-900">
                <Building2 className="w-4 h-4 mr-2" />
                Study Roles
              </TabsTrigger>
              <TabsTrigger value="training" className="text-slate-700 data-[state=active]:bg-red-600 data-[state=active]:text-slate-900">
                <GraduationCap className="w-4 h-4 mr-2" />
                Training
              </TabsTrigger>
            </TabsList>

            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64 h-9 bg-slate-100/80 border-slate-300 text-slate-900 placeholder:text-slate-500"
                data-testid="admin-search-input"
              />
            </div>
          </div>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="mt-0">
            {/* Study Filter for Dashboard */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Dashboard Metrics</h2>
              <div className="flex items-center gap-2">
                <Label className="text-slate-600 text-sm">Filter by Study:</Label>
                <Select value={dashboardStudyFilter} onValueChange={(val) => { setDashboardStudyFilter(val); fetchDashboardData(val); }}>
                  <SelectTrigger className="w-48 h-9 bg-slate-100/80 border-slate-300 text-slate-900">
                    <SelectValue placeholder="Select Study" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-100 border-slate-300">
                    <SelectItem value="all" className="text-gray-700 hover:bg-slate-200">All Studies</SelectItem>
                    {studies.map(study => (
                      <SelectItem key={study.id} value={study.id} className="text-gray-700 hover:bg-slate-200">{study.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {dashboardLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-red-500" />
              </div>
            ) : dashboardStats ? (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <div className="bg-slate-100/80 rounded-xl border border-slate-300 p-4 backdrop-blur-sm">
                    <div className="flex items-center gap-2 text-slate-600 mb-1">
                      <FileText className="w-4 h-4" />
                      <span className="text-xs font-medium">Documents</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{dashboardStats.total_documents}</p>
                  </div>
                  <div className="bg-slate-100/80 rounded-xl border border-slate-300 p-4 backdrop-blur-sm">
                    <div className="flex items-center gap-2 text-slate-600 mb-1">
                      <Users className="w-4 h-4" />
                      <span className="text-xs font-medium">Users</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{dashboardStats.total_users}</p>
                  </div>
                  <div className="bg-slate-100/80 rounded-xl border border-slate-300 p-4 backdrop-blur-sm">
                    <div className="flex items-center gap-2 text-slate-600 mb-1">
                      <FolderKanban className="w-4 h-4" />
                      <span className="text-xs font-medium">Studies</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{dashboardStats.total_studies}</p>
                  </div>
                  <div className="bg-slate-100/80 rounded-xl border border-slate-300 p-4 backdrop-blur-sm">
                    <div className="flex items-center gap-2 text-green-600 mb-1">
                      <Upload className="w-4 h-4" />
                      <span className="text-xs font-medium">Uploads Today</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600">{dashboardStats.uploads_today}</p>
                  </div>
                  <div className="bg-slate-100/80 rounded-xl border border-slate-300 p-4 backdrop-blur-sm">
                    <div className="flex items-center gap-2 text-red-600 mb-1">
                      <Download className="w-4 h-4" />
                      <span className="text-xs font-medium">Downloads Today</span>
                    </div>
                    <p className="text-2xl font-bold text-red-600">{dashboardStats.downloads_today}</p>
                  </div>
                  <div className="bg-slate-100/80 rounded-xl border border-slate-300 p-4 backdrop-blur-sm">
                    <div className="flex items-center gap-2 text-purple-600 mb-1">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-xs font-medium">Active Users</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-600">{dashboardStats.active_users_today}</p>
                  </div>
                </div>

                {/* Expiration Alerts */}
                {(dashboardStats.expiring_soon > 0 || dashboardStats.expired > 0) && (
                  <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-6 h-6 text-amber-600" />
                        <div>
                          <h3 className="font-semibold text-amber-300">Document Expiration Alerts</h3>
                          <p className="text-sm text-amber-600">
                            {dashboardStats.expired > 0 && <span className="font-medium text-red-600">{dashboardStats.expired} expired</span>}
                            {dashboardStats.expired > 0 && dashboardStats.expiring_soon > 0 && ' • '}
                            {dashboardStats.expiring_soon > 0 && <span>{dashboardStats.expiring_soon} expiring soon</span>}
                          </p>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={handleTriggerExpirationCheck} className="rounded-full bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200 hover:text-slate-900">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Send Alerts
                      </Button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Uploads Chart */}
                  <div className="lg:col-span-2 bg-slate-100/80 rounded-xl border border-slate-300 p-4 backdrop-blur-sm">
                    <h3 className="font-semibold text-slate-900 mb-4">Uploads (Last 7 Days)</h3>
                    <div className="flex items-end justify-between h-40 gap-2">
                      {uploadsChart.map((day, index) => (
                        <div key={day.date} className="flex-1 flex flex-col items-center">
                          <div 
                            className="w-full bg-red-500 rounded-t transition-all"
                            style={{ height: `${(day.uploads / maxUploads) * 100}%`, minHeight: day.uploads > 0 ? '8px' : '2px' }}
                          />
                          <span className="text-[10px] text-slate-500 mt-1">{day.date.slice(5)}</span>
                          <span className="text-xs font-medium text-slate-700">{day.uploads}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Top Users */}
                  <div className="bg-slate-100/80 rounded-xl border border-slate-300 p-4 backdrop-blur-sm">
                    <h3 className="font-semibold text-slate-900 mb-4">Most Active Users</h3>
                    <div className="space-y-3">
                      {topUsers.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">No activity yet</p>
                      ) : (
                        topUsers.map((u, index) => (
                          <div key={u.user_id} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                index === 0 ? 'bg-yellow-500/30 text-yellow-300' :
                                index === 1 ? 'bg-gray-300 text-slate-700' :
                                index === 2 ? 'bg-amber-500/30 text-amber-300' :
                                'bg-slate-200 text-slate-600'
                              }`}>
                                {index + 1}
                              </span>
                              <span className="text-sm font-medium text-slate-700">{u.user_name}</span>
                            </div>
                            <span className="text-sm text-slate-500">{u.activity_count} actions</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Documents by Status */}
                  <div className="bg-slate-100/80 rounded-xl border border-slate-300 p-4 backdrop-blur-sm">
                    <h3 className="font-semibold text-slate-900 mb-4">Documents by Status</h3>
                    <div className="space-y-2">
                      {Object.entries(dashboardStats.documents_by_status || {}).map(([status, count]) => (
                        <div key={status} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600 capitalize">{status.replace(/_/g, ' ')}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${
                                  status === 'approved' ? 'bg-green-500' :
                                  status === 'draft' ? 'bg-gray-400' :
                                  status === 'pending_review' ? 'bg-amber-500' :
                                  status === 'rejected' ? 'bg-red-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${(count / dashboardStats.total_documents) * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-slate-700 w-8">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="bg-slate-100/80 rounded-xl border border-slate-300 p-4 backdrop-blur-sm">
                    <h3 className="font-semibold text-slate-900 mb-4">Recent Activity</h3>
                    <ScrollArea className="h-48">
                      {recentActivity.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">No recent activity</p>
                      ) : (
                        <div className="space-y-3">
                          {recentActivity.map(activity => (
                            <div key={activity.id} className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                                {getActivityIcon(activity.action)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-slate-700">
                                  <span className="font-medium text-slate-900">{activity.user_name}</span>
                                  {' '}{activity.action}ed{' '}
                                  <span className="font-medium truncate">{activity.entity_name}</span>
                                </p>
                                <p className="text-xs text-slate-500">
                                  {new Date(activity.created_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                </div>

                {/* Expiration Settings */}
                <div className="bg-slate-100/80 rounded-xl border border-slate-300 p-4 backdrop-blur-sm">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Expiration Alert Settings
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-700">Warning Alert (days before expiration)</Label>
                      <Input
                        type="number"
                        value={expirationSettings.warning_days}
                        onChange={(e) => setExpirationSettings(prev => ({ ...prev, warning_days: parseInt(e.target.value) || 30 }))}
                        className="bg-slate-200 border-slate-400 text-slate-900"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700">Critical Alert (days before expiration)</Label>
                      <Input
                        type="number"
                        value={expirationSettings.critical_days}
                        onChange={(e) => setExpirationSettings(prev => ({ ...prev, critical_days: parseInt(e.target.value) || 7 }))}
                        className="bg-slate-200 border-slate-400 text-slate-900"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button onClick={handleSaveExpirationSettings} disabled={savingExpiration} className="rounded-full bg-red-600 hover:bg-blue-700 text-slate-900">
                        {savingExpiration ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Settings'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Expiring Documents List */}
                {(expiringDocs.length > 0 || expiredDocs.length > 0) && (
                  <div className="bg-slate-100/80 rounded-xl border border-slate-300 p-4 backdrop-blur-sm">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      Documents Requiring Attention
                    </h3>
                    <ScrollArea className="h-48">
                      <div className="space-y-2">
                        {expiredDocs.map(doc => (
                          <div key={doc.id} className="flex items-center justify-between p-2 bg-red-500/20 border border-red-500/30 rounded-lg">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 bg-red-500/30 text-red-300 text-xs rounded font-medium">EXPIRED</span>
                              <span className="text-sm font-medium text-slate-700">{doc.name}</span>
                            </div>
                            <span className="text-xs text-red-600">{doc.expiration_date?.slice(0, 10)}</span>
                          </div>
                        ))}
                        {expiringDocs.map(doc => {
                          const daysLeft = Math.ceil((new Date(doc.expiration_date) - new Date()) / (1000 * 60 * 60 * 24));
                          const isCritical = daysLeft <= expirationSettings.critical_days;
                          return (
                            <div key={doc.id} className={`flex items-center justify-between p-2 rounded-lg border ${isCritical ? 'bg-amber-500/20 border-amber-500/30' : 'bg-yellow-500/20 border-yellow-500/30'}`}>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 text-xs rounded font-medium ${isCritical ? 'bg-amber-500/30 text-amber-300' : 'bg-yellow-500/30 text-yellow-300'}`}>
                                  {daysLeft} days
                                </span>
                                <span className="text-sm font-medium text-slate-700">{doc.name}</span>
                              </div>
                              <span className="text-xs text-slate-500">{doc.expiration_date?.slice(0, 10)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <BarChart3 className="w-12 h-12 mb-3 text-slate-400" />
                <p>Click to load dashboard data</p>
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-0">
            <div className="bg-slate-100/80 rounded-xl border border-slate-300 shadow-lg backdrop-blur-sm">
              <div className="p-4 border-b border-slate-300 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">User Management</h2>
                  <p className="text-sm text-slate-600">Manage user roles and permissions</p>
                </div>
                <Button
                  onClick={() => setAddUserModal(true)}
                  className="h-9 rounded-full bg-red-600 hover:bg-blue-700 text-slate-900"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </div>

              <ScrollArea className="h-[calc(100vh-300px)]">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u, index) => (
                      <tr key={u.id} className="animate-fade-in" style={{ opacity: 0, animationDelay: `${index * 0.03}s` }}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
                              <User className="w-4 h-4 text-slate-600" />
                            </div>
                            <span className="font-medium text-slate-900">{u.name}</span>
                          </div>
                        </td>
                        <td className="text-slate-600">{u.email}</td>
                        <td>
                          <Select
                            value={u.role}
                            onValueChange={(value) => handleUpdateRole(u.id, value)}
                            disabled={u.id === user?.id}
                          >
                            <SelectTrigger className="w-32 h-8 bg-slate-200 border-slate-400 text-slate-900" data-testid={`role-select-${u.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-100 border-slate-300">
                              <SelectItem value="admin" className="text-gray-700 hover:bg-slate-200">
                                <div className="flex items-center gap-2">
                                  <Shield className="w-3 h-3 text-red-600" />
                                  Admin
                                </div>
                              </SelectItem>
                              <SelectItem value="editor" className="text-gray-700 hover:bg-slate-200">
                                <div className="flex items-center gap-2">
                                  <Edit className="w-3 h-3 text-teal-400" />
                                  Editor
                                </div>
                              </SelectItem>
                              <SelectItem value="viewer" className="text-gray-700 hover:bg-slate-200">
                                <div className="flex items-center gap-2">
                                  <User className="w-3 h-3 text-slate-600" />
                                  Viewer
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="text-slate-500 text-sm">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditUser(u)}
                              className="h-8 w-8 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {u.id !== user?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteUser(u.id)}
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-300 hover:bg-red-500/20"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Studies Tab */}
          <TabsContent value="studies" className="mt-0">
            <div className="bg-slate-100/80 rounded-xl border border-slate-300 shadow-lg backdrop-blur-sm">
              <div className="p-4 border-b border-slate-300 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Study Management</h2>
                  <p className="text-sm text-slate-600">Create and manage clinical studies</p>
                </div>
              </div>

              <ScrollArea className="h-[calc(100vh-300px)]">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Study Name</th>
                      <th>Description</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudies.map((study, index) => (
                      <tr key={study.id} className="animate-fade-in" style={{ opacity: 0, animationDelay: `${index * 0.03}s` }}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center">
                              <FolderKanban className="w-4 h-4 text-red-600" />
                            </div>
                            <span className="font-medium text-slate-900">{study.name}</span>
                          </div>
                        </td>
                        <td className="text-slate-600 max-w-xs truncate">
                          {study.description || '-'}
                        </td>
                        <td className="text-slate-500 text-sm">
                          {new Date(study.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditStudy(study)}
                              className="h-8 w-8 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                              data-testid={`edit-study-${study.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteStudy(study.id)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-300 hover:bg-red-500/20"
                              data-testid={`delete-study-${study.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Audit Logs Tab */}
          <TabsContent value="audit" className="mt-0">
            <div className="bg-slate-100/80 rounded-xl border border-slate-300 shadow-lg backdrop-blur-sm">
              <div className="p-4 border-b border-slate-300 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Audit Trail</h2>
                  <p className="text-sm text-slate-600">Complete activity log for compliance</p>
                </div>
                <div className="flex items-center gap-4">
                  {/* Study Filter for Audit Logs */}
                  <div className="flex items-center gap-2">
                    <Label className="text-slate-600 text-sm">Filter by Study:</Label>
                    <Select value={auditStudyFilter} onValueChange={(val) => { setAuditStudyFilter(val); fetchAuditLogs(val); }}>
                      <SelectTrigger className="w-48 h-9 bg-slate-100/80 border-slate-300 text-slate-900">
                        <SelectValue placeholder="Select Study" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-100 border-slate-300">
                        <SelectItem value="all" className="text-gray-700 hover:bg-slate-200">All Studies</SelectItem>
                        {studies.map(study => (
                          <SelectItem key={study.id} value={study.id} className="text-gray-700 hover:bg-slate-200">{study.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {auditStats && (
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">{auditStats.total_logs}</p>
                      <p className="text-xs text-slate-500">Total Events</p>
                    </div>
                  )}
                </div>
              </div>

              {auditStats && (
                <div className="p-4 border-b border-slate-300 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-2 text-green-600">
                      <Upload className="w-4 h-4" />
                      <span className="text-sm font-medium">Uploads</span>
                    </div>
                    <p className="text-xl font-bold text-green-300 mt-1">
                      {(auditStats.actions_breakdown?.DOCUMENT_UPLOADED || 0) + (auditStats.actions_breakdown?.DOCUMENT_VERSION_UPLOADED || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                    <div className="flex items-center gap-2 text-red-600">
                      <Eye className="w-4 h-4" />
                      <span className="text-sm font-medium">Views</span>
                    </div>
                    <p className="text-xl font-bold text-blue-300 mt-1">
                      {(auditStats.actions_breakdown?.DOCUMENT_VIEWED || 0) + (auditStats.actions_breakdown?.DOCUMENT_VERSION_VIEWED || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-amber-500/20 border border-amber-500/30 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-600">
                      <Edit className="w-4 h-4" />
                      <span className="text-sm font-medium">Updates</span>
                    </div>
                    <p className="text-xl font-bold text-amber-300 mt-1">
                      {auditStats.actions_breakdown?.DOCUMENT_UPDATED || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                    <div className="flex items-center gap-2 text-red-600">
                      <Trash2 className="w-4 h-4" />
                      <span className="text-sm font-medium">Deletions</span>
                    </div>
                    <p className="text-xl font-bold text-red-300 mt-1">
                      {auditStats.actions_breakdown?.DOCUMENT_DELETED || 0}
                    </p>
                  </div>
                </div>
              )}

              <ScrollArea className="h-[calc(100vh-450px)]">
                {auditLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-red-500" />
                  </div>
                ) : auditLogs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                    <Activity className="w-12 h-12 mb-3 text-slate-400" />
                    <p>No audit logs yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200/50">
                    {auditLogs.map((log, index) => (
                      <div 
                        key={log.id} 
                        className="p-4 hover:bg-slate-200/30 animate-fade-in"
                        style={{ opacity: 0, animationDelay: `${index * 0.02}s` }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              log.action.includes('UPLOAD') ? 'bg-green-500/20 text-green-600' :
                              log.action.includes('DELETE') ? 'bg-red-500/20 text-red-600' :
                              log.action.includes('VIEW') ? 'bg-red-500/20 text-red-600' :
                              'bg-slate-200 text-slate-600'
                            }`}>
                              {log.action.includes('UPLOAD') ? <Upload className="w-4 h-4" /> :
                               log.action.includes('DELETE') ? <Trash2 className="w-4 h-4" /> :
                               log.action.includes('VIEW') ? <Eye className="w-4 h-4" /> :
                               <Edit className="w-4 h-4" />}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{log.action.replace(/_/g, ' ')}</p>
                              <p className="text-sm text-slate-600">{log.entity_name}</p>
                              {log.details && <p className="text-xs text-slate-500 mt-1">{log.details}</p>}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-slate-600">{log.user_name}</p>
                            <p className="text-xs text-slate-500">{new Date(log.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="mt-0">
            <div className="bg-slate-100/80 rounded-xl border border-slate-300 shadow-lg backdrop-blur-sm">
              <div className="p-4 border-b border-slate-300">
                <h2 className="text-lg font-semibold text-slate-900">Email Notification Logs</h2>
                <p className="text-sm text-slate-600">All simulated email notifications (mock mode)</p>
              </div>

              <ScrollArea className="h-[calc(100vh-300px)]">
                {notificationLogs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                    <Bell className="w-12 h-12 mb-3 text-slate-400" />
                    <p>No notifications sent yet</p>
                    <p className="text-sm">Notifications will be logged here when triggered</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200/50">
                    {notificationLogs.map((log, index) => (
                      <div key={log.id} className="p-4 hover:bg-slate-200/30">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs rounded font-medium border ${
                              log.notification_type === 'document_upload' ? 'bg-green-500/20 text-green-600 border-green-500/30' :
                              log.notification_type === 'workflow_change' ? 'bg-red-500/20 text-red-600 border-red-500/30' :
                              log.notification_type === 'expiration_alert' ? 'bg-amber-500/20 text-amber-600 border-amber-500/30' :
                              'bg-slate-200 text-slate-600 border-slate-400'
                            }`}>
                              {log.notification_type?.replace(/_/g, ' ').toUpperCase() || 'EMAIL'}
                            </span>
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-600 border border-purple-500/30 text-xs rounded font-medium">
                              {log.status.toUpperCase()}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500">{new Date(log.created_at).toLocaleString()}</span>
                        </div>
                        <p className="font-medium text-slate-900 mb-1">{log.subject}</p>
                        <p className="text-sm text-slate-600">To: {log.recipient_name} &lt;{log.recipient_email}&gt;</p>
                        <details className="mt-2">
                          <summary className="text-xs text-red-600 cursor-pointer hover:text-blue-300">View body</summary>
                          <pre className="mt-2 p-2 bg-slate-50 rounded text-xs text-slate-600 whitespace-pre-wrap border border-slate-300">{log.body}</pre>
                        </details>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Duplicate Detection Tab */}
          <TabsContent value="duplicates" className="mt-0">
            <DuplicateDetectionPanel />
          </TabsContent>

          {/* Email Ingestion Tab */}
          <TabsContent value="email-ingestion" className="mt-0">
            <EmailIngestionPanel />
          </TabsContent>

          {/* Time-Bound Access Tab */}
          <TabsContent value="time-access" className="mt-0">
            <TimeBoundAccessPanel />
          </TabsContent>

          {/* User Study Assignments Tab */}
          <TabsContent value="study-assignments" className="mt-0">
            <UserStudyAssignmentsPanel />
          </TabsContent>

          {/* Training Management Tab */}
          <TabsContent value="training" className="mt-0">
            <TrainingManagementPanel />
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Study Modal */}
      <Dialog open={editStudyModal} onOpenChange={setEditStudyModal}>
        <DialogContent className="sm:max-w-md bg-slate-50 border-slate-300 text-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Edit Study</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-700">Study Name</Label>
              <Input
                value={studyName}
                onChange={(e) => setStudyName(e.target.value)}
                className="bg-slate-100 border-slate-300 text-slate-900 placeholder:text-slate-500"
                data-testid="edit-study-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Description</Label>
              <Input
                value={studyDescription}
                onChange={(e) => setStudyDescription(e.target.value)}
                className="bg-slate-100 border-slate-300 text-slate-900 placeholder:text-slate-500"
                data-testid="edit-study-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditStudyModal(false)} className="rounded-full bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200 hover:text-slate-900">
              Cancel
            </Button>
            <Button
              onClick={handleSaveStudy}
              disabled={saving}
              className="rounded-full bg-red-600 hover:bg-blue-700 text-slate-900"
              data-testid="save-study-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit User Modal */}
      <Dialog open={addUserModal || editUserModal} onOpenChange={(open) => { setAddUserModal(false); setEditUserModal(false); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-red-600" />
              {editUserModal ? 'Edit User' : 'Add New User'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-800 dark:text-slate-200 flex items-center gap-2">
                <User className="w-4 h-4" /> Basic Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-700 dark:text-slate-300">Full Name *</Label>
                  <Input
                    value={newUserData.name}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter full name"
                    className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 dark:text-slate-300">Email Address *</Label>
                  <Input
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Enter email address"
                    disabled={editUserModal}
                    className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>
              {!editUserModal && (
                <div className="space-y-2">
                  <Label className="text-slate-700 dark:text-slate-300">Password *</Label>
                  <Input
                    type="password"
                    value={newUserData.password}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Enter password"
                    className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Role</Label>
                <Select value={newUserData.role} onValueChange={(val) => { handleRoleChange(val); applyRolePreset(val); }}>
                  <SelectTrigger className="w-full bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 max-h-[300px]">
                    {/* Clinical Trial Roles */}
                    <div className="px-2 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900">System Administration</div>
                    <SelectItem value="system_admin" className="text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <Shield className="w-3 h-3 text-red-600" />
                        System Administrator
                      </div>
                    </SelectItem>
                    
                    <div className="px-2 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 mt-1">TMF Management</div>
                    <SelectItem value="tmf_lead" className="text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <FolderKanban className="w-3 h-3 text-purple-500" />
                        TMF Lead / Manager
                      </div>
                    </SelectItem>
                    <SelectItem value="ctm" className="text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <Users className="w-3 h-3 text-blue-500" />
                        Clinical Trial Manager (CTM)
                      </div>
                    </SelectItem>
                    
                    <div className="px-2 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 mt-1">Clinical Operations</div>
                    <SelectItem value="cra" className="text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <User className="w-3 h-3 text-teal-500" />
                        CRA / Monitor
                      </div>
                    </SelectItem>
                    <SelectItem value="cdm" className="text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <FileText className="w-3 h-3 text-green-500" />
                        Clinical Data Manager
                      </div>
                    </SelectItem>
                    
                    <div className="px-2 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 mt-1">Quality & Regulatory</div>
                    <SelectItem value="qc_reviewer" className="text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <Eye className="w-3 h-3 text-orange-500" />
                        QC Reviewer
                      </div>
                    </SelectItem>
                    <SelectItem value="qa_auditor" className="text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <Search className="w-3 h-3 text-amber-500" />
                        QA / Auditor
                      </div>
                    </SelectItem>
                    <SelectItem value="regulatory" className="text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <FileText className="w-3 h-3 text-indigo-500" />
                        Regulatory Affairs
                      </div>
                    </SelectItem>
                    
                    <div className="px-2 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 mt-1">External Access</div>
                    <SelectItem value="site_user" className="text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <User className="w-3 h-3 text-cyan-500" />
                        Site User (PI / Coordinator)
                      </div>
                    </SelectItem>
                    <SelectItem value="vendor" className="text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <Users className="w-3 h-3 text-slate-500" />
                        CRO / Vendor User
                      </div>
                    </SelectItem>
                    <SelectItem value="inspector" className="text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <Eye className="w-3 h-3 text-red-500" />
                        Inspector (Read-Only)
                      </div>
                    </SelectItem>
                    
                    {/* Legacy roles for backward compatibility */}
                    <div className="px-2 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 mt-1">Legacy Roles</div>
                    <SelectItem value="admin" className="text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <Shield className="w-3 h-3 text-gray-400" />
                        Admin (Legacy)
                      </div>
                    </SelectItem>
                    <SelectItem value="editor" className="text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <Edit className="w-3 h-3 text-gray-400" />
                        Editor (Legacy)
                      </div>
                    </SelectItem>
                    <SelectItem value="viewer" className="text-slate-700 dark:text-slate-200">
                      <div className="flex items-center gap-2">
                        <User className="w-3 h-3 text-gray-400" />
                        Viewer (Legacy)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Selecting a role will apply default permissions based on clinical trial role definitions. Segregation of duties is enforced.
                </p>
              </div>
            </div>

            {/* Granular Permissions */}
            <div className="space-y-4 border-t border-slate-200 dark:border-slate-600 pt-4">
              <h4 className="font-medium text-slate-800 dark:text-slate-200 flex items-center gap-2">
                <Shield className="w-4 h-4" /> Granular Permissions
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {/* Document Permissions */}
                <div className="space-y-2 p-3 bg-slate-100 dark:bg-slate-700/50 rounded-lg">
                  <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300">Document Actions</h5>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={newUserData.permissions?.can_upload || false}
                      onChange={(e) => setNewUserData(prev => ({
                        ...prev,
                        permissions: { ...prev.permissions, can_upload: e.target.checked }
                      }))}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-400">Can Upload Documents</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={newUserData.permissions?.can_edit_metadata || false}
                      onChange={(e) => setNewUserData(prev => ({
                        ...prev,
                        permissions: { ...prev.permissions, can_edit_metadata: e.target.checked }
                      }))}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-400">Can Edit Metadata</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={newUserData.permissions?.can_delete || false}
                      onChange={(e) => setNewUserData(prev => ({
                        ...prev,
                        permissions: { ...prev.permissions, can_delete: e.target.checked }
                      }))}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-400">Can Delete Documents</span>
                  </label>
                </div>

                {/* Workflow Permissions */}
                <div className="space-y-2 p-3 bg-slate-100 dark:bg-slate-700/50 rounded-lg">
                  <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300">Workflow & Signatures</h5>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={newUserData.permissions?.can_review || false}
                      onChange={(e) => setNewUserData(prev => ({
                        ...prev,
                        permissions: { ...prev.permissions, can_review: e.target.checked }
                      }))}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-400">Can Review Documents</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={newUserData.permissions?.can_approve || false}
                      onChange={(e) => setNewUserData(prev => ({
                        ...prev,
                        permissions: { ...prev.permissions, can_approve: e.target.checked }
                      }))}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-400">Can Approve Documents</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={newUserData.permissions?.can_sign || false}
                      onChange={(e) => setNewUserData(prev => ({
                        ...prev,
                        permissions: { ...prev.permissions, can_sign: e.target.checked }
                      }))}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-400">Can Add E-Signatures</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={newUserData.permissions?.can_manage_workflows || false}
                      onChange={(e) => setNewUserData(prev => ({
                        ...prev,
                        permissions: { ...prev.permissions, can_manage_workflows: e.target.checked }
                      }))}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-400">Can Manage Workflows</span>
                  </label>
                </div>
              </div>

              {/* Study Access */}
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h5 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Study Access</h5>
                <p className="text-xs text-blue-600 dark:text-blue-400 mb-2">
                  Leave empty to allow access to all studies. Select specific studies to limit access.
                </p>
                <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                  {studies.map(study => (
                    <label key={study.id} className="flex items-center gap-1.5 bg-white dark:bg-slate-700 px-2 py-1 rounded border border-slate-200 dark:border-slate-600 cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={newUserData.permissions?.visible_studies?.includes(study.id) || false}
                        onChange={(e) => {
                          const currentStudies = newUserData.permissions?.visible_studies || [];
                          const newStudies = e.target.checked 
                            ? [...currentStudies, study.id]
                            : currentStudies.filter(id => id !== study.id);
                          setNewUserData(prev => ({
                            ...prev,
                            permissions: { ...prev.permissions, visible_studies: newStudies }
                          }));
                        }}
                        className="rounded border-slate-300"
                      />
                      <span className="text-xs text-slate-700 dark:text-slate-300">{study.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Permission Summary */}
            <div className="bg-slate-100 dark:bg-slate-700/50 rounded-lg p-3">
              <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Permission Summary</h5>
              <div className="flex flex-wrap gap-1">
                {newUserData.permissions?.can_upload && <span className="text-[10px] px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Upload</span>}
                {newUserData.permissions?.can_review && <span className="text-[10px] px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">Review</span>}
                {newUserData.permissions?.can_approve && <span className="text-[10px] px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">Approve</span>}
                {newUserData.permissions?.can_sign && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">Sign</span>}
                {newUserData.permissions?.can_edit_metadata && <span className="text-[10px] px-2 py-0.5 rounded bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400">Edit</span>}
                {newUserData.permissions?.can_delete && <span className="text-[10px] px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">Delete</span>}
                {newUserData.permissions?.can_manage_workflows && <span className="text-[10px] px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">Workflows</span>}
                {(!newUserData.permissions?.visible_studies || newUserData.permissions?.visible_studies.length === 0) && 
                  <span className="text-[10px] px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300">All Studies</span>
                }
                {newUserData.permissions?.visible_studies?.length > 0 && 
                  <span className="text-[10px] px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300">{newUserData.permissions.visible_studies.length} Studies</span>
                }
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddUserModal(false); setEditUserModal(false); }} className="rounded-full bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">
              Cancel
            </Button>
            <Button
              onClick={editUserModal ? handleUpdateUser : handleAddUser}
              disabled={addingUser}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              {addingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : (editUserModal ? 'Update User' : 'Add User')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
